import { useState } from 'react';
import { ArrowUp, ArrowDown, Filter } from 'lucide-react';
import { useApp } from '../../contexts/AppContext';

export default function TransactionHistory() {
  const { transactions, houses, getHouse } = useApp();
  const [filterHouseId, setFilterHouseId] = useState<string | null>(null);

  const filteredTransactions = filterHouseId
    ? transactions.filter(t => t.houseId === filterHouseId)
    : transactions;

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    }).format(date);
  };

  return (
    <div className="backdrop-blur-lg bg-white/10 rounded-2xl border border-white/20 overflow-hidden">
      {/* Filter Bar */}
      <div className="p-4 border-b border-white/10 flex items-center space-x-4">
        <Filter size={20} className="text-white/60" />
        <select
          value={filterHouseId || ''}
          onChange={(e) => setFilterHouseId(e.target.value || null)}
          className="bg-white/5 border border-white/20 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-white/40"
        >
          <option value="">All Houses</option>
          {houses.filter(h => !h.isArchived && !h.deactivatedOn).map(house => (
            <option key={house.id} value={house.id}>
              {house.icon} {house.name}
            </option>
          ))}
        </select>
        <span className="text-white/40 text-sm">
          {filteredTransactions.length} transactions
        </span>
      </div>

      {/* Transaction List */}
      <div className="max-h-[600px] overflow-y-auto">
        {filteredTransactions.length === 0 ? (
          <div className="p-12 text-center text-white/40">
            No transactions yet
          </div>
        ) : (
          <div className="divide-y divide-white/10">
            {filteredTransactions.map(transaction => {
              const house = getHouse(transaction.houseId);
              const isPositive = transaction.pointsDelta > 0;

              return (
                <div
                  key={transaction.id}
                  className="p-4 flex items-center justify-between hover:bg-white/5 transition-colors"
                >
                  <div className="flex items-center space-x-4">
                    <div
                      className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        isPositive ? 'bg-green-500/20' : 'bg-red-500/20'
                      }`}
                    >
                      {isPositive ? (
                        <ArrowUp className="text-green-400" size={20} />
                      ) : (
                        <ArrowDown className="text-red-400" size={20} />
                      )}
                    </div>
                    <div>
                      <div className="flex items-center space-x-2">
                        <span className="text-xl">{house?.icon}</span>
                        <span className="text-white font-medium">{house?.name}</span>
                      </div>
                      <div className="flex items-center space-x-2 text-sm text-white/60">
                        <span className="capitalize">{transaction.source}</span>
                        <span>•</span>
                        <span>{formatDate(transaction.timestamp)}</span>
                      </div>
                    </div>
                  </div>
                  <div
                    className={`text-xl font-bold ${
                      isPositive ? 'text-green-400' : 'text-red-400'
                    }`}
                  >
                    {isPositive ? '+' : ''}{transaction.pointsDelta}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
